import { useEffect, useState } from 'react';
import axios from 'axios';
import { Tag, Copy, Check, X } from 'lucide-react';

const API = window.location.origin + '/api';
const DISMISS_KEY = 'promo_dismissed';

function labelForCoupon(c) {
  if (c.type === 'percent') return `${Math.round(c.value)}% OFF`;
  if (c.type === 'fixed') return `K${Math.round(c.value)} OFF`;
  if (c.type === 'free_delivery') return 'FREE DELIVERY';
  return 'DEAL';
}

function fineprint(c) {
  const parts = [];
  if (c.min_order_amount > 0) parts.push(`min K${Math.round(c.min_order_amount)}`);
  if (c.valid_until) parts.push(`until ${c.valid_until}`);
  return parts.join(' · ');
}

export default function PromoCodeStrip() {
  const [coupons, setCoupons] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]')); }
    catch { return new Set(); }
  });
  const [copied, setCopied] = useState('');

  useEffect(() => {
    axios.get(`${API}/coupons/public`)
      .then(r => setCoupons(r.data.coupons || []))
      .catch(() => setCoupons([]));
  }, []);

  const dismiss = (code) => {
    const next = new Set(dismissed);
    next.add(code);
    setDismissed(next);
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...next])); } catch (e) { /* ignore */ }
  };

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied(''), 1500);
    } catch (e) { /* ignore */ }
  };

  const visible = coupons.filter(c => !dismissed.has(c.code));
  if (visible.length === 0) return null;

  return (
    <div className="promo-strip-wrap" style={{ maxWidth: 1100, margin: '4px auto 0', padding: '0 16px' }}>
      <div className="promo-strip" data-testid="promo-strip" style={{
        display: 'flex', gap: 10, overflowX: 'auto', padding: '10px 0',
        scrollSnapType: 'x mandatory',
      }}>
        {visible.map(c => (
          <div
            key={c.code}
            data-testid={`promo-card-${c.code}`}
            style={{
              flexShrink: 0, minWidth: 260, scrollSnapAlign: 'start',
              display: 'flex', alignItems: 'center', gap: 12,
              background: 'linear-gradient(135deg, #b6e34d 0%, #86efac 100%)',
              color: '#0a1f44', padding: '10px 14px', borderRadius: 12,
              boxShadow: '0 2px 8px rgba(10,31,68,0.08)', position: 'relative',
            }}
          >
            <div style={{ background: '#0a1f44', color: '#b6e34d', width: 40, height: 40, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Tag size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: 0.04, opacity: 0.85 }}>
                {labelForCoupon(c)}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                <code style={{ background: 'rgba(10,31,68,0.15)', padding: '2px 8px', borderRadius: 4, fontSize: '0.88rem', fontWeight: 700, letterSpacing: '0.05em' }}>
                  {c.code}
                </code>
                <button
                  onClick={() => copy(c.code)}
                  data-testid={`promo-copy-${c.code}`}
                  style={{ background: 'transparent', border: 'none', color: '#0a1f44', cursor: 'pointer', padding: 2 }}
                  aria-label={`Copy ${c.code}`}
                >
                  {copied === c.code ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
              {(c.description || fineprint(c)) && (
                <div style={{ fontSize: '0.68rem', opacity: 0.75, marginTop: 2 }}>
                  {c.description || fineprint(c)}
                </div>
              )}
            </div>
            <button
              onClick={() => dismiss(c.code)}
              data-testid={`promo-dismiss-${c.code}`}
              style={{ background: 'transparent', border: 'none', color: '#0a1f44', opacity: 0.55, cursor: 'pointer', padding: 4, alignSelf: 'flex-start' }}
              aria-label="Dismiss"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
